import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SITE_URL, company } from "@/lib/company-facts";

const SITE_NAME = company.legalName;
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  /** JSON-LD structured data — pass an object or array of objects */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Override canonical path (defaults to current pathname, sin prefijo de idioma) */
  pathOverride?: string;
  /** Excluir la página del índice (rutas utilitarias, no comerciales) */
  noindex?: boolean;
  /** og:type — "website" por defecto, "article" para contenido editorial */
  ogType?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
}

const SEO = ({
  title,
  description,
  image,
  jsonLd,
  pathOverride,
  noindex = false,
  ogType = "website",
  publishedTime,
  modifiedTime,
}: SEOProps) => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();

  const currentLang = lang || i18n.language || "es";
  // Strip leading "/{lang}" from pathname to get base path
  const basePath = pathOverride ?? (location.pathname.replace(/^\/(es|en|pt)/, "") || "/");
  const cleanBase = basePath === "" ? "/" : basePath.replace(/\/$/, "") || "/";

  const suffix = cleanBase === "/" ? "" : cleanBase;
  const canonical = `${SITE_URL}/${currentLang}${suffix}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  const localeMap: Record<string, string> = {
    es: "es_ES",
    en: "en_US",
    pt: "pt_BR",
  };

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* hreflang alternates */}
      {(["es", "en", "pt"] as const).map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}/${l}${suffix}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/es${suffix}`} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={localeMap[currentLang]} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
